import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { Takeover } from '@/components/ui/Takeover';
import { toast } from '@/components/ui/Toaster';
import { formsApi, projectsApi, type FormDetail } from '@/lib/api';

export type MoveFormMode = 'move' | 'duplicate';

interface MoveFormModalProps {
  form: FormDetail;
  mode: MoveFormMode;
  /** Submitted responses on the form — move is refused once there are any. */
  submittedCount: number;
  /** Outstanding (not yet submitted) responses, for the move warning. */
  outstandingCount: number;
  onClose: () => void;
}

interface ProjectOption {
  id: string;
  name: string;
  group: string;
}

/**
 * Move or duplicate a form into another project. Projects are grouped by
 * client, because many are just "Support Program" (docs/forms.md → "Moving
 * and duplicating").
 */
export function MoveFormModal({ form, mode, submittedCount, outstandingCount, onClose }: MoveFormModalProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [projectId, setProjectId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const moving = mode === 'move';
  const blocked = moving && submittedCount > 0;

  const { data: projects, isLoading } = useQuery({
    queryKey: ['projects'],
    queryFn: () => projectsApi.list(),
  });

  const groups = useMemo(() => {
    const options: ProjectOption[] = (Array.isArray(projects) ? projects : [])
      .filter((p: any) => !p.archivedAt && !(moving && p.id === form.projectId))
      .map((p: any) => ({
        id: p.id,
        name: p.name,
        group: p.clientOrganization?.name ?? p.organization?.name ?? 'Other',
      }));
    const byGroup = new Map<string, ProjectOption[]>();
    for (const o of options) byGroup.set(o.group, [...(byGroup.get(o.group) ?? []), o]);
    return [...byGroup.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([group, items]) => ({ group, items: items.sort((a, b) => a.name.localeCompare(b.name)) }));
  }, [projects, moving, form.projectId]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['formsManage'] });
    queryClient.invalidateQueries({ queryKey: ['projectForms'] });
    queryClient.invalidateQueries({ queryKey: ['myForms'] });
  };

  const targetName = () => groups.flatMap((g) => g.items).find((p) => p.id === projectId)?.name ?? 'the project';

  const mutation = useMutation({
    mutationFn: async () => {
      if (moving) return formsApi.move(form.id, projectId);
      return { form: await formsApi.duplicate(form.id, projectId), removedAssignees: [] as string[] };
    },
    onSuccess: ({ form: result, removedAssignees }) => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: ['form', form.id] });
      const where = targetName();
      if (moving) {
        toast({
          type: 'success',
          title: `Moved to ${where}`,
          message: removedAssignees.length
            ? `Removed the assignment for ${removedAssignees.join(', ')}, who ${removedAssignees.length === 1 ? "isn't" : "aren't"} on that project.`
            : undefined,
        });
      } else {
        toast({ type: 'success', title: `Copied to ${where}`, message: 'You are now editing the copy. It starts as a draft.' });
      }
      onClose();
      navigate(`/forms/manage/${result.id}`);
    },
    onError: (e: any) => setError(e?.message || (moving ? "Couldn't move the form" : "Couldn't copy the form")),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!projectId) return setError('Choose a project.');
    setError(null);
    mutation.mutate();
  };

  return (
    <Takeover onClose={onClose} maxWidthClass="max-w-md">
      <div className="rounded-xl bg-white shadow-xl">
        <div className="p-6 pb-4">
          <h2 className="text-heading-3 text-secondary">{moving ? 'Move form' : 'Duplicate to a project'}</h2>
          <p className="mt-1 text-sm text-grey-500">
            {moving
              ? `“${form.title}” and its outstanding assignments move to the project you choose.`
              : `A draft copy of “${form.title}” with the same questions. No responses or assignments are copied.`}
          </p>
        </div>

        {blocked ? (
          <div className="px-6 pb-6">
            <p className="text-sm text-secondary">
              This form has {submittedCount} submitted {submittedCount === 1 ? 'response' : 'responses'}, which stay with{' '}
              {form.project?.name ?? 'its current project'}. Duplicate it to the other project instead.
            </p>
            <div className="flex justify-end pt-5">
              <Button type="button" variant="ghost" onClick={onClose}>
                Close
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5 px-6 pb-6">
            <div>
              <label htmlFor="move-form-project" className="mb-1.5 block text-sm font-medium text-secondary">
                Project
              </label>
              <select
                id="move-form-project"
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
                className="input w-full"
                disabled={isLoading}
              >
                <option value="">{isLoading ? 'Loading projects…' : 'Choose a project…'}</option>
                {groups.map((g) => (
                  <optgroup key={g.group} label={g.group}>
                    {g.items.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                        {!moving && p.id === form.projectId ? ' (this project)' : ''}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>

            {moving && outstandingCount > 0 && (
              <p className="text-sm text-grey-600">
                {outstandingCount} {outstandingCount === 1 ? 'person has' : 'people have'} this form outstanding. Anyone who isn't on
                the new project loses the assignment, along with any answers they've started.
              </p>
            )}

            {error && (
              <p className="text-sm text-error" role="alert">
                {error}
              </p>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" isLoading={mutation.isPending}>
                {moving ? 'Move' : 'Duplicate'}
              </Button>
            </div>
          </form>
        )}
      </div>
    </Takeover>
  );
}
